import React, { useCallback, useEffect, useRef } from 'react';
import { Image } from '@mantine/core';
import { Video, useVideoContext } from '@gfazioli/mantine-video';
import { resolveMediaObjectFit, mediaFitInline, mediaFitVars } from './mediaFit';
import { useVideoReport } from '../tracking/videoTrackingContext';
import VideoPlayerBridge from './VideoPlayerBridge';
import classes from './MediaFrame.module.css';

function PauseOnUnmount() {
  const { videoRef } = useVideoContext();
  useEffect(() => {
    const video = videoRef.current;
    return () => video?.pause();
  }, [videoRef]);
  return null;
}

/**
 * Lecteur vidéo Mantine (inline ou fond hero via asBackground).
 */
export function MediaVideo({
  src,
  poster = '',
  playback = {},
  asBackground = false,
  mediaFit = 'cover',
  isEditing = false,
  alt = 'Vidéo',
  className = '',
  style,
  radius = 0,
  intrinsicSize = false,
  onError,
  onPlayerState,
  stopOnUnmount = false,
}) {
  /* Le lecteur Mantine expose déjà ce qu'il faut : aucune bibliothèque de plus,
     et aucun `<video>` à aller chercher dans le DOM. Ces rappels sont inertes
     hors site publié — le contexte de suivi y vaut `null`. */
  const report = useVideoReport();
  const durationRef = useRef(0);
  const onPlayChange = useCallback((playing) => (
    playing ? report.onPlay() : report.onPause()
  ), [report]);
  const onDurationChange = useCallback((seconds) => { durationRef.current = seconds; }, []);
  const onCurrentTimeChange = useCallback((seconds) => (
    report.onTime(seconds, durationRef.current)
  ), [report]);

  const fit = resolveMediaObjectFit(mediaFit);
  const fitInline = mediaFitInline(mediaFit);
  const fitVars = mediaFitVars(mediaFit);
  const pb = playback || {};

  const showPosterOnly = isEditing && asBackground && poster;
  if (showPosterOnly) {
    return (
      <Image
        src={poster}
        alt={alt}
        fit={fit}
        radius={radius}
        className={`${classes.media} ${className}`.trim()}
        style={{ ...fitInline, ...style }}
      />
    );
  }

  const autoPlay = !isEditing && !!pb.autoplay;
  const muted = pb.muted === true || autoPlay;
  /* Une vidéo qui démarre seule ne dit rien du lecteur : un fond de Hero ou de
     Bloc part muet dès l'affichage, et le compter comme « lancée » attribuait à
     un destinataire un geste qu'il n'a pas fait. Les fonds sont donc écartés,
     et la lecture automatique avec eux — même raison. */
  const tracked = !asBackground && !pb.autoplay;
  const loop = pb.loop ?? asBackground;
  const controls = !asBackground && pb.controls !== false;

  const mediaClass = [
    intrinsicSize ? classes.mediaIntrinsic : classes.media,
    className,
  ].filter(Boolean).join(' ');

  return (
    <Video
      ref={tracked ? report.rootRef : undefined}
      onPlayChange={tracked ? onPlayChange : undefined}
      onDurationChange={tracked ? onDurationChange : undefined}
      onCurrentTimeChange={tracked ? onCurrentTimeChange : undefined}
      src={src}
      poster={poster || undefined}
      autoPlay={autoPlay}
      muted={muted}
      loop={loop}
      controls={controls}
      playsInline
      asBackground={asBackground && !intrinsicSize}
      backgroundMuteButton={asBackground && !isEditing && !intrinsicSize}
      fit={fit}
      clickToToggle={!asBackground && !intrinsicSize}
      shortcuts={!asBackground && !intrinsicSize}
      variant={asBackground && !intrinsicSize ? 'minimal' : 'overlay'}
      radius={radius}
      className={mediaClass}
      style={intrinsicSize ? { ...fitInline, width: '100%', height: 'auto', ...style } : { ...fitVars, ...fitInline, ...style }}
      preload={isEditing ? 'metadata' : 'auto'}
      onError={onError}
    >
      {onPlayerState || stopOnUnmount ? (
        <>
          {controls && <Video.Controls />}
          {onPlayerState && <VideoPlayerBridge onPlayerState={onPlayerState} />}
          {stopOnUnmount && <PauseOnUnmount />}
        </>
      ) : undefined}
    </Video>
  );
}
